// src/app/core/userflow.service.ts
import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import userflow, { Attributes, EventAttributes } from 'userflow.js';
import { environment } from '@hub-env/environment';
import { User } from './user.service';

@Injectable({ providedIn: 'root' })
export class UserflowService {
  private readonly router = inject(Router);
  private initialized = false;
  private identifiedUserId: string | null = null;

  init(): void {
    if (this.initialized || !environment.USERFLOW_TOKEN) return;

    userflow.init(environment.USERFLOW_TOKEN);

    // Let Userflow use Angular SPA navigation instead of full page reloads
    userflow.setCustomNavigate((url: string) => {
      const parsed = new URL(url, window.location.origin);
      const target =
        parsed.pathname + parsed.search + parsed.hash;

      if (parsed.origin === window.location.origin) {
        void this.router.navigateByUrl(target);
      } else {
        window.location.href = url;
      }
    });

    // Helpful when troubleshooting route timing in SPAs
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        // Give Angular one paint/frame so DOM targets exist before flows attach
        requestAnimationFrame(() => {
          // no-op: keeping this hook here is useful if you later want
          // route-specific track() or start() calls.
        });
      });

    this.initialized = true;
  }

  async identify(user: User): Promise<void> {
    if (!this.initialized) this.init();
    if (!this.initialized) return;

    const { id, ...attributes } = user;
    if (id === undefined) return;

    const userId = id.toString();
    await userflow.identify(userId, attributes);
    this.identifiedUserId = userId;
  }

  async updateUser(attributes: Attributes): Promise<void> {
    if (!this.initialized) return;
    await userflow.updateUser(attributes);
  }

  async track(eventName: string, attributes: EventAttributes = {}): Promise<void> {
    if (!this.initialized) return;
    await userflow.track(eventName, attributes);
  }

  reset(): void {
    userflow.reset();
    this.initialized = false;
    this.identifiedUserId = null;
  }

  get isIdentified(): boolean {
    return !!this.identifiedUserId;
  }
}
